/**
 * The Email tab, and the three questions inside it.
 *
 * Sub-tabs rather than one long page: "which server" and "what does it say" are
 * asked at different times by the same person, and stacking them meant the
 * notification list sat below a fold of SMTP fields. The outer Site settings
 * tab row stays at four rather than growing to six.
 *
 * The sub-tab is in the URL alongside the outer one (?tab=email&section=notifications)
 * so a link to a specific screen still works.
 */

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTabList } from '../../components/ui'
import { SiteSmtpSettings } from './SmtpForm'
import { EmailTemplates } from './EmailTemplates'
import { EmailOutbox } from './EmailOutbox'
import './email.css'

type Section = 'server' | 'notifications' | 'log'

const SECTIONS: [Section, string][] = [
  ['server', 'Mail server'],
  ['notifications', 'Notifications'],
  ['log', 'Delivery log'],
]

export function SiteEmailSettings() {
  const tabs = useTabList()
  const [params, setParams] = useSearchParams()
  const requested = params.get('section') as Section | null
  const [fallback, setFallback] = useState<Section>('server')
  const section: Section =
    requested && SECTIONS.some(([key]) => key === requested) ? requested : fallback

  const setSection = (next: Section) => {
    setFallback(next)
    const updated = new URLSearchParams(params)
    updated.set('section', next)
    setParams(updated, { replace: true })
  }

  return (
    <div className="tc-email-sections">
      <div
        className="tc-tabs tc-email-subtabs"
        ref={tabs.ref}
        onKeyDown={tabs.onKeyDown}
        role="tablist"
        aria-label="Email settings sections"
      >
        {SECTIONS.map(([key, label]) => (
          <button
            key={key}
            id={`tc-email-${key}-tab`}
            role="tab"
            aria-controls={`tc-email-${key}-panel`}
            aria-selected={section === key}
            className={`tc-tab ${section === key ? 'is-active' : ''}`}
            onClick={() => setSection(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div
        id={`tc-email-${section}-panel`}
        role="tabpanel"
        aria-labelledby={`tc-email-${section}-tab`}
      >
        {section === 'server' && <SiteSmtpSettings />}
        {section === 'notifications' && <EmailTemplates />}
        {section === 'log' && <EmailOutbox />}
      </div>
    </div>
  )
}
